import { doc, getDoc } from 'firebase/firestore';

import { db } from './firebase';
import { mapBeerReviewDoc } from './mapBeerReviewDoc';
import { formatBreweryConsumptionLabel } from './brewery/formatBreweryConsumptionLabel';
import { formatVenueConsumptionLabel } from './brewery/formatVenueConsumptionLabel';

/**
 * @param {Map<string, object | null>} cache
 * @param {string} collectionName
 * @param {string} docId
 * @returns {Promise<object | null>}
 */
async function getCachedDocData(cache, collectionName, docId) {
  if (typeof docId !== 'string' || docId.length === 0) {
    return null;
  }
  if (cache.has(docId)) {
    return cache.get(docId);
  }
  try {
    const snapshot = await getDoc(doc(db, collectionName, docId));
    const data = snapshot.exists() ? snapshot.data() : null;
    cache.set(docId, data);
    return data;
  } catch (fetchError) {
    console.error(`Error fetching ${collectionName}/${docId}:`, fetchError);
    cache.set(docId, null);
    return null;
  }
}

/**
 * @param {import('firebase/firestore').QueryDocumentSnapshot} reviewDoc
 * @param {string} beerName
 * @param {object} [caches]
 * @param {Map<string, object | null>} [caches.breweryCache]
 * @param {Map<string, object | null>} [caches.userCache]
 * @param {Map<string, object | null>} [caches.consumptionBreweryCache]
 * @param {Map<string, object | null>} [caches.consumptionVenueCache]
 * @returns {Promise<object>}
 */
export async function enrichBeerReviewDoc(reviewDoc, beerName = 'Unknown Beer', caches = null) {
  const reviewData = reviewDoc.data();
  const breweryId = reviewData.breweryId;
  const userId = reviewData.uid;

  let breweryName = 'Unknown Brewery';
  if (breweryId) {
    if (caches?.breweryCache) {
      const breweryData = await getCachedDocData(caches.breweryCache, 'breweries', breweryId);
      if (breweryData?.name) {
        breweryName = breweryData.name;
      }
    } else {
      try {
        const breweryDoc = await getDoc(doc(db, 'breweries', breweryId));
        if (breweryDoc.exists()) {
          breweryName = breweryDoc.data().name || breweryName;
        }
      } catch (fetchError) {
        console.error(`Error fetching brewery ${breweryId}:`, fetchError);
      }
    }
  }

  let userName = 'Unknown User';
  if (userId) {
    if (caches?.userCache) {
      const userData = await getCachedDocData(caches.userCache, 'users', userId);
      if (userData) {
        userName = userData.userName || userData.displayName || userName;
      }
    } else {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userName = userData.userName || userData.displayName || userName;
        }
      } catch (fetchError) {
        console.error(`Error fetching user ${userId}:`, fetchError);
      }
    }
  }

  const consumptionPlaceType = typeof reviewData.consumptionPlaceType === 'string'
    ? reviewData.consumptionPlaceType.trim()
    : '';
  let consumptionPlaceName = null;
  if (consumptionPlaceType === 'brewery') {
    const consumptionPlaceId = typeof reviewData.consumptionPlaceId === 'string'
      ? reviewData.consumptionPlaceId.trim()
      : '';
    if (consumptionPlaceId.length > 0) {
      if (caches?.consumptionBreweryCache) {
        const consumptionBreweryData = await getCachedDocData(
          caches.consumptionBreweryCache,
          'breweries',
          consumptionPlaceId,
        );
        if (consumptionBreweryData) {
          consumptionPlaceName = formatBreweryConsumptionLabel(consumptionBreweryData);
        }
      } else {
        try {
          const consumptionBreweryDoc = await getDoc(doc(db, 'breweries', consumptionPlaceId));
          if (consumptionBreweryDoc.exists()) {
            consumptionPlaceName = formatBreweryConsumptionLabel(consumptionBreweryDoc.data());
          }
        } catch (fetchError) {
          console.error(`Error fetching consumption brewery ${consumptionPlaceId}:`, fetchError);
        }
      }
    }
  } else if (consumptionPlaceType === 'venue') {
    const consumptionPlaceId = typeof reviewData.consumptionPlaceId === 'string'
      ? reviewData.consumptionPlaceId.trim()
      : '';
    if (consumptionPlaceId.length > 0) {
      if (caches?.consumptionVenueCache) {
        const consumptionVenueData = await getCachedDocData(
          caches.consumptionVenueCache,
          'venues',
          consumptionPlaceId,
        );
        if (consumptionVenueData) {
          consumptionPlaceName = formatVenueConsumptionLabel(consumptionVenueData);
        }
      } else {
        try {
          const consumptionVenueDoc = await getDoc(doc(db, 'venues', consumptionPlaceId));
          if (consumptionVenueDoc.exists()) {
            consumptionPlaceName = formatVenueConsumptionLabel(consumptionVenueDoc.data());
          }
        } catch (fetchError) {
          console.error(`Error fetching consumption venue ${consumptionPlaceId}:`, fetchError);
        }
      }
    }
  } else if (consumptionPlaceType === 'event') {
    const rawEventName = typeof reviewData.consumptionPlaceName === 'string'
      ? reviewData.consumptionPlaceName.trim()
      : '';
    if (rawEventName.length > 0) {
      consumptionPlaceName = rawEventName;
    }
  }

  return mapBeerReviewDoc({
    reviewId: reviewDoc.id,
    reviewData,
    beerName,
    breweryName,
    userName,
    consumptionPlaceName,
  });
}

/**
 * @param {import('firebase/firestore').QueryDocumentSnapshot[]} reviewDocs
 * @param {string} [beerName]
 * @returns {Promise<object[]>}
 */
export function enrichBeerReviewDocs(reviewDocs, beerName = 'Unknown Beer') {
  const caches = {
    breweryCache: new Map(),
    userCache: new Map(),
    consumptionBreweryCache: new Map(),
    consumptionVenueCache: new Map(),
  };
  return Promise.all(
    reviewDocs.map((reviewDoc) => enrichBeerReviewDoc(reviewDoc, beerName, caches)),
  );
}

/**
 * @param {string} beerId
 * @returns {Promise<string>}
 */
export async function fetchBeerName(beerId) {
  let beerName = 'Unknown Beer';
  try {
    const beerDocRef = doc(db, 'beer', beerId);
    const beerDoc = await getDoc(beerDocRef);
    if (beerDoc.exists()) {
      beerName = beerDoc.data().name || beerName;
    }
  } catch (fetchError) {
    console.error(`Error fetching beer ${beerId}:`, fetchError);
  }
  return beerName;
}
